import React from "react";
import styled from "styled-components";
import { CheckCircle } from "react-feather";
import { Button } from "../../../atoms";
import { spacing } from "../../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../../design-system/tokens/typography";
import { rawColors } from "../../../../design-system/tokens/colors";
import { borderRadius } from "../../../../design-system/tokens/borders";
import { OutlinePrimaryButton } from "../ConnectorWizardModal.styles";
import type { StepRenderProps } from "../ConnectorWizardModal.types";

/** Mirrors SelectStep / PreviewStep / TestStep STEP_MIN_HEIGHT so the modal
 *  doesn't shrink when navigating into the Finish state. */
const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[3]};
  width: 100%;
  min-height: 460px;
  padding: ${spacing[6]} 0;
`;

const SuccessBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[2]};
  border-radius: ${borderRadius.full};
  background-color: #dcfce7;
`;

const SuccessIcon = styled(CheckCircle)`
  width: 32px;
  height: 32px;
  color: ${rawColors.accent.green500};
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing[1]};
  margin-top: ${spacing[1]};
`;

const Title = styled.h2`
  margin: 0;
  ${typographyMixin("subtitle", 2)}
  color: ${rawColors.text.highEmphasis};
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  text-align: center;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  margin-top: ${spacing[4]};
`;

/** Dark-navy filled CTA — Figma's `primary/900` button used by the
 *  "View connector" action. Mirrors `OutlinePrimaryButton`'s sizing so both
 *  buttons sit on the same baseline. */
const PrimaryDarkButton = styled(Button).attrs({
  variant: "primary-dark" as const,
  size: "md" as const,
})`
  &&& {
    height: 36px;
    padding: ${spacing[2]} ${spacing[4]};
    border-radius: ${borderRadius.lg};
    box-shadow: none;
  }
`;

/**
 * FinishStep — terminal success state. The connection is already persisted
 * by the time we land here (Preview "Import" creates it), so this step's
 * only job is to confirm and offer the two follow-up actions from Figma:
 *
 *   - "Preview and prepare" → onFinishView  (parent: profile, then open Data Studio)
 *   - "Add another instance" → onFinishRestart (resets wizard to step 0)
 */
export const FinishStep: React.FC<StepRenderProps> = ({
  values,
  onFinishView,
  onFinishRestart,
}) => {
  const connectionName =
    typeof values.connection_name === "string" && values.connection_name.trim()
      ? (values.connection_name as string)
      : "your data source";

  return (
    <Wrap aria-live="polite">
      <SuccessBadge>
        <SuccessIcon aria-hidden="true" />
      </SuccessBadge>
      <TitleBlock>
        <Title>Connection added successful</Title>
        <Subtitle>Connected to {connectionName}</Subtitle>
      </TitleBlock>
      <Actions>
        <PrimaryDarkButton onClick={onFinishView}>
          Preview and prepare
        </PrimaryDarkButton>
        <OutlinePrimaryButton onClick={onFinishRestart}>
          Add another instance
        </OutlinePrimaryButton>
      </Actions>
    </Wrap>
  );
};

export default FinishStep;
